import {withSlotsPlaceholders} from '@wix/widget-plugins-ooi'
import {viewerDataHooks as DH} from '@wix/wix-events-data-hooks'
import {TPAComponentsProvider} from '@wix/wix-ui-tpa/cssVars'
import {useEnvironment, useErrorBoundary, usePanorama} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect} from 'react'
import {ErrorPage} from '../../../../../commons/components/error-page'
import {FocusHandler} from '../../../../../commons/components/focus-handler'
import {Direction} from '../../../../../commons/constants/html'
import {DatesProvider} from '../../../../../commons/hooks/dates'
import {StylesForAllBreakpointsProvider} from '../../../../../commons/hooks/styles-for-all-breakpoints'
import {ROOT_COMPONENT_ID} from '../../constants'
import {useMobileSize} from '../../hooks/full-width'
import {Modals} from '../modals'
import {Router} from '../router'
import {DetailsPageRuntimeContext} from '../runtime-context/context'
import s from './app.scss'
import {DetailsPageAppProps} from './interfaces'

const DetailsPage: React.FC<DetailsPageAppProps> = props => {
  const {isRTL, isMobile, isEditorX: responsive} = useEnvironment()
  const mobileSize = useMobileSize()
  const {error} = useErrorBoundary()
  const {errorMonitor} = usePanorama()

  const mobile = isMobile || mobileSize
  const {state, formViewState, actions, pageReadyTimestamp} = props

  useEffect(() => {
    actions.handleFormViewState(formViewState)
  }, [formViewState, pageReadyTimestamp])

  useEffect(() => {
    if (state?.navigation?.shouldNavigateBack) {
      window.history.back()
    }
  }, [state])

  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        actions.handleCacheRestore()
      }
    }

    // Cheap way to inform Sled about end of hydration
    document.getElementById(ROOT_COMPONENT_ID)?.setAttribute('data-hydrated', 'true')

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  useEffect(() => {
    if (!state || error) {
      errorMonitor().reportError(
        new Error('Failed to load details page widget', {cause: error || 'State is not loaded'}),
      )
    }
  }, [error, state])

  if (!state || error) {
    return <ErrorPage />
  }

  return (
    <DetailsPageRuntimeContext.Provider value={props}>
      <TPAComponentsProvider value={{mobile, rtl: isRTL}}>
        <DatesProvider dates={state.dates}>
          <StylesForAllBreakpointsProvider>
            <div
              dir={isRTL ? Direction.RTL : Direction.LTR}
              id={ROOT_COMPONENT_ID}
              className={s.root}
              data-hook={DH.ROOT_NODE}
            >
              <Modals />
              <FocusHandler />
              <div
                className={classNames(s.root, {
                  'events-details-page-mobile': mobile,
                  'events-details-page-desktop': !mobile,
                  'events-details-page-responsive': responsive,
                  'events-details-page-nonresponsive': !responsive,
                })}
              >
                <Router />
              </div>
            </div>
          </StylesForAllBreakpointsProvider>
        </DatesProvider>
      </TPAComponentsProvider>
    </DetailsPageRuntimeContext.Provider>
  )
}

export const App = withSlotsPlaceholders(DetailsPage)
